import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  ChevronDown,
  ChevronUp,
  Clock3,
  HelpCircle,
  TrendingDown,
  TrendingUp,
  Wallet,
} from "lucide-react";
import generateAvatarUrl from "../../utils/generateAvatarUrl";
import {
  BOND_DRAW_INVESTOR_FIELD_TIPS,
  computeInvestorPoolAggregate,
  formatDrawCoin,
  formatDrawDateTime,
  formatInvestorProfitDisplay,
  getInvestorSharePercent,
} from "../../utils/bondDrawHelpers";
import {
  bondDrawInvestorCashout,
  bondDrawInvestorToggleActive,
  fetchBondDrawInvestors,
  fetchBondDrawPool,
  selectBondDrawInvestorActionLoading,
} from "../../redux/bondDrawSlice";
import BondDrawCashoutModal from "./BondDrawCashoutModal";
import BondPlayConfirmModal from "../bond-play/BondPlayConfirmModal";

const FieldHelp = ({ tip }) => (
  <button type="button" className="bdraw-investor-help" title={tip} aria-label={tip}>
    <HelpCircle size={11} strokeWidth={2.25} />
  </button>
);

const SummaryRow = ({ label, tip, value, valueClass = "" }) => (
  <div className="bdraw-investor-summary-row">
    <span className="bdraw-investor-summary-label">
      {label}
      {tip ? <FieldHelp tip={tip} /> : null}
    </span>
    <strong className={valueClass}>{value}</strong>
  </div>
);

const InvestorRecentSummary = ({ recent }) => (
  <div className="bdraw-investor-summary">
    <SummaryRow
      label="Investment"
      tip={BOND_DRAW_INVESTOR_FIELD_TIPS.investment}
      value={formatDrawCoin(recent.investment)}
    />
    <SummaryRow
      label="Pool Size"
      tip={BOND_DRAW_INVESTOR_FIELD_TIPS.poolSize}
      value={formatDrawCoin(recent.poolSize)}
    />
    <SummaryRow
      label="Your Share"
      tip={BOND_DRAW_INVESTOR_FIELD_TIPS.share}
      value={`${getInvestorSharePercent(recent)}%`}
      valueClass="share"
    />
    {!!(recent.used || recent.refunded) ? (
      <SummaryRow
        label="Used / Refunded"
        tip={BOND_DRAW_INVESTOR_FIELD_TIPS.usedRefunded}
        value={`${formatDrawCoin(recent.used || 0)} / ${formatDrawCoin(recent.refunded || 0)}`}
      />
    ) : null}
    {!!(recent.poolUsed || recent.poolRefunded) ? (
      <SummaryRow
        label="Pool Used / Refunded"
        tip={BOND_DRAW_INVESTOR_FIELD_TIPS.poolUsedRefunded}
        value={`${formatDrawCoin(recent.poolUsed || 0)} / ${formatDrawCoin(recent.poolRefunded || 0)}`}
      />
    ) : null}
  </div>
);

const InvestorProfitCard = ({ recent }) => {
  const profit = Number(recent.profit || 0);
  const positive = profit >= 0;
  const aggregate = computeInvestorPoolAggregate(recent);

  return (
    <div className={`bdraw-investor-profit${positive ? " won" : " lost"}`}>
      <div className="bdraw-investor-profit-head">
        {positive ? (
          <TrendingUp size={15} strokeWidth={2.5} />
        ) : (
          <TrendingDown size={15} strokeWidth={2.5} />
        )}
        <span>{positive ? "Your Profit" : "Your Loss"}</span>
        <FieldHelp tip={BOND_DRAW_INVESTOR_FIELD_TIPS.yourPnl} />
      </div>
      <strong className={positive ? "win" : "loss"}>
        {positive ? "+" : ""}
        {formatDrawCoin(profit)}
      </strong>
      {aggregate ? (
        <div className="bdraw-investor-pool-agg">
          <FieldHelp
            tip={
              aggregate.kind === "profit"
                ? BOND_DRAW_INVESTOR_FIELD_TIPS.poolProfit
                : BOND_DRAW_INVESTOR_FIELD_TIPS.poolLoss
            }
          />
          <span>{aggregate.kind === "profit" ? "Pool Profit" : "Pool Loss"}:</span>
          <strong className={aggregate.kind === "profit" ? "win" : "loss"}>
            {aggregate.kind === "profit" ? "+" : ""}
            {formatDrawCoin(Math.round(aggregate.amount))}
          </strong>
        </div>
      ) : null}
    </div>
  );
};

const InvestorGameHistoryCard = ({ game, recent }) => {
  const profit = Number(game.profit || 0);
  const positive = profit >= 0;

  return (
    <div className="bdraw-investor-history-card">
      <div className="bdraw-investor-history-head">
        <span className="bdraw-investor-history-date">
          {formatDrawDateTime(game.date, game.time) || "—"}
        </span>
        <span className={`bdraw-player-status ${game.status === "won" ? "won" : "lost"}`}>
          {game.status === "won" ? "WON" : "LOST"}
        </span>
      </div>
      <div className="bdraw-investor-history-body">
        <SummaryRow
          label="Investment"
          tip={BOND_DRAW_INVESTOR_FIELD_TIPS.investment}
          value={formatDrawCoin(game.investment)}
        />
        <SummaryRow
          label="Your Share"
          tip={BOND_DRAW_INVESTOR_FIELD_TIPS.share}
          value={`${getInvestorSharePercent(game)}%`}
          valueClass="share"
        />
        <SummaryRow
          label="Potential"
          tip={BOND_DRAW_INVESTOR_FIELD_TIPS.potential}
          value={formatDrawCoin(game.potentialWin)}
        />
        {game.refunded > 0 ? (
          <SummaryRow
            label="Refunded"
            tip={BOND_DRAW_INVESTOR_FIELD_TIPS.refunded}
            value={formatDrawCoin(game.refunded)}
            valueClass="warn"
          />
        ) : null}
        {!!(game.used || game.refunded) ? (
          <SummaryRow
            label="Used / Refunded"
            tip={BOND_DRAW_INVESTOR_FIELD_TIPS.usedRefunded}
            value={`${formatDrawCoin(game.used || 0)} / ${formatDrawCoin(game.refunded || 0)}`}
          />
        ) : null}
        <div className="bdraw-investor-history-result">
          <span className="bdraw-investor-summary-label">
            {positive ? "Your Profit" : "Your Loss"}
            <FieldHelp tip={BOND_DRAW_INVESTOR_FIELD_TIPS.yourPnl} />
          </span>
          <strong className={positive ? "win" : "loss"}>
            {positive ? "+" : ""}
            {formatDrawCoin(profit)}
          </strong>
        </div>
        {profit < 0 && typeof recent?.poolRefunded === "number" ? (
          <SummaryRow
            label="Pool Loss"
            value={formatDrawCoin(recent.poolRefunded)}
            valueClass="loss"
          />
        ) : null}
      </div>
    </div>
  );
};

const BondDrawInvestorCard = ({ investor, isCurrentUser = false, onToast }) => {
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState(false);
  const [showCashout, setShowCashout] = useState(false);
  const [toggleConfirm, setToggleConfirm] = useState(null);
  const actionLoading = useSelector(selectBondDrawInvestorActionLoading);

  const { recent, recentGames = [] } = investor;
  const avatarUrl = generateAvatarUrl(investor.name || "Investor");
  const active = Number(investor.active || 0);
  const pending = Number(investor.pending || 0);
  const profit = Number(recent?.profit || 0);
  const isActiveInvestor = !!investor.activeStatus;

  const refreshInvestorData = () => {
    dispatch(fetchBondDrawPool());
    dispatch(fetchBondDrawInvestors({ limit: 100 }));
  };

  const handleCashout = async (amount) => {
    const result = await dispatch(bondDrawInvestorCashout({ amount }));
    if (bondDrawInvestorCashout.fulfilled.match(result)) {
      setShowCashout(false);
      refreshInvestorData();
      onToast?.(`Withdrew ${formatDrawCoin(amount)} COIN to your wallet.`);
    } else {
      onToast?.(result.payload || "Cash out failed.");
    }
  };

  const handleToggleActive = async () => {
    const nextActive = toggleConfirm?.nextActive;
    if (typeof nextActive !== "boolean") return;

    const result = await dispatch(bondDrawInvestorToggleActive({ isActive: nextActive }));
    if (bondDrawInvestorToggleActive.fulfilled.match(result)) {
      setToggleConfirm(null);
      refreshInvestorData();
      onToast?.(nextActive ? "Investor account activated." : "Investor account deactivated.");
    } else {
      onToast?.(result.payload || "Could not update investor status.");
    }
  };

  const openToggleConfirm = () => {
    const nextActive = !investor.activeStatus;
    setToggleConfirm({
      nextActive,
      title: nextActive ? "Activate investor account?" : "Deactivate investor account?",
      description: nextActive ? (
        <p>Your active balance will be used in new rounds automatically.</p>
      ) : (
        <p>
          Your balance will stop being used for new rounds. Ongoing reservations will complete
          normally.
        </p>
      ),
      confirmLabel: nextActive ? "Activate" : "Deactivate",
      tone: nextActive ? "primary" : "danger",
    });
  };

  const renderCurrentUserActions = () => {
    if (!isCurrentUser) return null;
    return (
      <div className="bdraw-investor-actions">
        <button
          type="button"
          className="bdraw-investor-action cashout"
          disabled={actionLoading || active <= 0}
          onClick={() => setShowCashout(true)}
        >
          <span className="bdraw-investor-action-label">Cash Out</span>
          <strong>{formatDrawCoin(active)} COIN</strong>
        </button>
        <button
          type="button"
          className={`bdraw-investor-action ${investor.activeStatus ? "deactivate" : "activate"}`}
          disabled={actionLoading}
          onClick={openToggleConfirm}
        >
          <span className="bdraw-investor-action-label">
            {investor.activeStatus ? "Deactivate" : "Activate"}
          </span>
        </button>
      </div>
    );
  };

  return (
    <>
      <li className={`bdraw-player-card bdraw-investor-card${expanded ? " expanded" : ""}${isCurrentUser ? " mine" : ""}`}>
        <button
          type="button"
          className="bdraw-player-card-toggle"
          onClick={() => setExpanded((prev) => !prev)}
          aria-expanded={expanded}
        >
          <img className="bdraw-player-avatar" src={avatarUrl} alt="" />
          <span className="bdraw-player-head-main">
            <span className="bdraw-player-name-row">
              <strong>{investor.name || "Anonymous"}</strong>
              {isCurrentUser ? <span className="bdraw-player-you">YOU</span> : null}
            </span>
          </span>
          <span className="bdraw-investor-head-stats">
            <span className={`bdraw-investor-stat${isActiveInvestor ? " on" : " paused"}`} title="Active balance">
              <Wallet size={11} strokeWidth={2.5} />
              {formatDrawCoin(active)}
            </span>
            <span className="bdraw-investor-stat pending" title="Held in reserve">
              <Clock3 size={11} strokeWidth={2.5} />
              {formatDrawCoin(pending)}
            </span>
            <span className={`bdraw-investor-stat ${profit >= 0 ? "won" : "lost"}`} title="Last round P/L">
              {profit >= 0 ? (
                <TrendingUp size={11} strokeWidth={2.5} />
              ) : (
                <TrendingDown size={11} strokeWidth={2.5} />
              )}
              {formatInvestorProfitDisplay(profit)}
            </span>
          </span>
          <span className="bdraw-player-chevron" aria-hidden>
            {expanded ? <ChevronUp size={14} strokeWidth={2.5} /> : <ChevronDown size={14} strokeWidth={2.5} />}
          </span>
        </button>

        {expanded ? (
          <div className="bdraw-player-card-body">
            {recent ? (
              <>
                <div className="bdraw-player-meta-row">
                  <span className="bdraw-player-meta">
                    {formatDrawDateTime(recent.date, recent.time) || "—"}
                  </span>
                  <span className={`bdraw-player-status ${recent.status === "won" ? "won" : "lost"}`}>
                    {recent.status === "won" ? "WON" : "LOST"}
                  </span>
                </div>
                <InvestorRecentSummary recent={recent} />
                <InvestorProfitCard recent={recent} />
              </>
            ) : (
              <p className="bdraw-player-empty-detail">No recent round activity yet.</p>
            )}

            {renderCurrentUserActions()}

            {isCurrentUser && recentGames.length > 0 ? (
                  <div className="bdraw-investor-history">
                    <span className="bdraw-player-history-title">Recent Games</span>
                    {recentGames.map((game, idx) => (
                      <InvestorGameHistoryCard key={`ig-${idx}`} game={game} recent={recent} />
                    ))}
                  </div>
                ) : null}
          </div>
        ) : null}
      </li>

      {isCurrentUser ? (
        <>
          <BondDrawCashoutModal
            open={showCashout}
            maxAmount={active}
            loading={actionLoading}
            onClose={() => !actionLoading && setShowCashout(false)}
            onSubmit={handleCashout}
          />
          <BondPlayConfirmModal
            open={!!toggleConfirm}
            eyebrow="Investor account"
            title={toggleConfirm?.title}
            description={toggleConfirm?.description}
            confirmLabel={toggleConfirm?.confirmLabel || "Confirm"}
            tone={toggleConfirm?.tone || "primary"}
            loading={actionLoading}
            onClose={() => !actionLoading && setToggleConfirm(null)}
            onConfirm={handleToggleActive}
          />
        </>
      ) : null}
    </>
  );
};

export default BondDrawInvestorCard;
